/**
 * The client-side access-verdict cache, and the one way to invalidate it.
 *
 * 🚨 WHY THIS FILE EXISTS. PremiumContentGate caches the /api/user/subscription-status verdict in
 * sessionStorage for 5 minutes so navigation does not spin. Nothing ever cleared it — the only
 * references in the codebase were the key, one read and one write.
 *
 * So a customer who paid still had `{hasAccess:false}` cached in their tab, and the next GATED page
 * they opened told them to subscribe. Customer, 2026-08-08: "Payed my monthly and when I hit full
 * access it asked me to pay aagain." The workaround given at the time was "try ctrl shift r", which
 * is precisely the act of clearing this cache — the symptom named its own cause and nobody read it
 * that way. The /subscribe PAGE was fixed the same day (see subscribe-offered-checkout-after-payment)
 * but that fix only covered the success page; every other gated surface kept the stale verdict for up
 * to 5 minutes, and the background refresh does not even fire for the first 2.5.
 *
 * ⚠ ANY new post-payment path must call clearSubscriptionCache(). Access changed on the SERVER; the
 * cache is the only thing that can still say otherwise.
 */

export const SUB_CACHE_KEY = 'dt_sub_cache'
export const SUB_CACHE_TTL_MS = 5 * 60 * 1000

/** Drop the cached access verdict so the next gate render asks the server. Safe in private mode. */
export function clearSubscriptionCache(): void {
  try {
    sessionStorage.removeItem(SUB_CACHE_KEY)
  } catch {
    /* private mode / SSR — nothing cached means nothing to clear */
  }
}

/**
 * True when this page load is a return from Stripe checkout. Stripe's success_url is
 * `/subscribe?success=1&session_id=...` for both subscriptions and passes, and `/donate/success`
 * for donations — all of which change access server-side and therefore invalidate the cache.
 */
export function isPostCheckoutReturn(search: string, pathname = ''): boolean {
  try {
    const p = new URLSearchParams(search)
    return p.get('success') === '1' || p.has('session_id') || pathname.startsWith('/donate/success')
  } catch {
    return false
  }
}
